import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Page } from '@shared/api';
import { productApi } from '../api/product-api';
import type { ProductListQuery, ProductRequest, ProductView } from './types';

export const productKeys = {
  all: ['products'] as const,
  list: (q: ProductListQuery) => [...productKeys.all, 'list', q] as const,
  detail: (id: string) => [...productKeys.all, 'detail', id] as const,
};

export function useProductsQuery(query: ProductListQuery) {
  return useQuery({
    queryKey: productKeys.list(query),
    queryFn: () => productApi.list(query),
    staleTime: 30_000,
  });
}

function findCachedProduct(qc: ReturnType<typeof useQueryClient>, id: string): ProductView | undefined {
  const detail = qc.getQueryData<ProductView>(productKeys.detail(id));
  if (detail) return detail;
  const entries = qc.getQueriesData<Page<ProductView>>({
    queryKey: [...productKeys.all, 'list'],
  });
  for (const [, page] of entries) {
    const hit = page?.items?.find((p) => p.id === id);
    if (hit) return hit;
  }
  return undefined;
}

export function useProductQuery(id: string | undefined) {
  const qc = useQueryClient();
  // List rows carry the full ProductView shape, so we always prefer the cached
  // row from the list/detail caches and only hit the network when there is no
  // cached entry (e.g. user opened the edit URL directly). This avoids a
  // duplicate GET after PUT and keeps a broken/500 detail endpoint from
  // blocking the form.
  const cached = id ? findCachedProduct(qc, id) : undefined;
  return useQuery({
    queryKey: productKeys.detail(id ?? ''),
    queryFn: () => productApi.get(id!),
    enabled: Boolean(id) && !cached,
    initialData: cached,
    staleTime: Infinity,
  });
}

export function useCreateProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProductRequest) => productApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useUpdateProductMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProductRequest) => productApi.update(id, body),
    onSuccess: (data) => {
      // Patch both detail and every cached list row in-place — no
      // invalidation, so no follow-up GETs are issued after PUT.
      qc.setQueryData(productKeys.detail(id), data);
      qc.setQueriesData<Page<ProductView>>(
        { queryKey: [...productKeys.all, 'list'] },
        (page) =>
          page
            ? { ...page, items: page.items.map((p) => (p.id === id ? data : p)) }
            : page,
      );
    },
  });
}

export function useSubmitProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productApi.submit(id),
    onSuccess: (data) => {
      qc.setQueryData(productKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useArchiveProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}
